import { useState, useMemo, useEffect, useCallback } from "react";
import Fuse from "fuse.js";

// -------------------- UTILS --------------------
import { CATEGORY_MAP } from "../utils/categorymapping";
import { translations } from "../utils/translations";
import { auditPrice } from "../utils/priceAuditor";

/**
 * ============================================================
 * GUIDE — HELPERS CAVE
 * ============================================================
 *
 * On détecte les produits cave via les marqueurs injectés plus haut
 * dans App.js ou renvoyés par le backend.
 */
const isCaveProduct = (p) => {
  if (!p || typeof p !== "object") return false;

  if (p.__isCave === true) return true;
  if (p.__productScope === "cave") return true;
  if (p.product_scope === "cave") return true;
  if (Number(p.isCave || 0) === 1) return true;
  if (p.source === "cave") return true;

  return false;
};

const normalizeLabel = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const isRealCategory = (value) => normalizeLabel(value) !== "";

/**
 * ============================================================
 * GUIDE — ENRICHIR LES TRADUCTIONS AUTOMATIQUES
 * ============================================================
 */
const enrichirTraductionsAuto = (categories, t) => {
  const updatedCategories = { ...t.categories };
  const DEFAULT_DE_TRANSLATIONS = {
    vin: "Wein",
    fromage_vache: "Kuhkäse",
    produits_divers: "Verschiedene Produkte",
    oeuf: "Eier",
    charcuterie: "Aufschnitt",
    produit_laitier: "Molkereiprodukte",
  };

  (Array.isArray(categories) ? categories : []).forEach((name) => {
    const safeName = normalizeLabel(name);
    if (!safeName) return;

    const slug =
      CATEGORY_MAP[safeName] || safeName.toLowerCase().replace(/\s+/g, "_");

    if (!updatedCategories[slug]) updatedCategories[slug] = safeName;

    const deKey = slug + "_de";
    if (!updatedCategories[deKey]) {
      updatedCategories[deKey] = DEFAULT_DE_TRANSLATIONS[slug] || safeName;
    }
  });

  return { ...t, categories: updatedCategories };
};

export const useAppLogic = (boutique, auth, panierContext, profilPricing) => {
  // -------------------- CONTEXTES --------------------
  const { user: userProfil = null, authStatus = "visitor" } = auth || {};
  const isAuthenticated = authStatus === "connected";

  const {
    produits = [],
    categories = [],
    catLoading = false,
    vueActuelle = "accueil",
    setVueActuelle = () => {},
    categorieActive = null,
    setCategorieActive = () => {},
    recherche = "",
    setRecherche = () => {},
  } = boutique || {};

  const { sousTotal = 0 } = panierContext || {};

  // -------------------- ÉTATS UI --------------------
  const [langue, setLangue] = useState(
    () => localStorage.getItem("lang") || "fr"
  );
  const [modeGestion, setModeGestion] = useState(false);
  const [modeLivraison, setModeLivraison] = useState("livraison");

  // -------------------- MODALS --------------------
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editProductModal, setEditProductModal] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formP, setFormP] = useState({
    name: "",
    category: "Fromage Vache",
    price: 0,
    old_price: 0,
    isOnline: 1,
    isDiscount: 0,
    histoire: "",
    unite: "KG",
    stock: 1,
  });

  const handleProductClick = useCallback(
    (prod) => setSelectedProduct(prod),
    []
  );
  const handleCloseModal = useCallback(() => setSelectedProduct(null), []);

  // -------------------- TRADUCTIONS --------------------
  const t = useMemo(() => {
    const baseT = translations[langue] || translations.fr;
    return enrichirTraductionsAuto(categories, baseT);
  }, [categories, langue]);

  /**
   * ============================================================
   * GUIDE — AUDIT DES PRODUITS
   * ============================================================
   *
   * On normalise tous les produits :
   * - audit pricing
   * - conservation des flags cave/classic
   */
  const produitsAudites = useMemo(() => {
    if (!Array.isArray(produits)) return [];

    return produits.map((p) => {
      const cave = isCaveProduct(p);

      if (p && p.__vm) {
        const vmAudit = p._audit || {
          pricingMode: p.pricingMode || "PUBLIC",
          availabilityStatus: p.isAvailable ? "AVAILABLE" : "HIDDEN",
          displayPrice: p.displayPrice ?? p.price ?? 0,
          displayBasePrice: p.basePrice ?? null,
          displaySavingsTag:
            p._audit?.displaySavingsTag ?? p._audit?.discountLabel ?? null,
          isPromoActive: Boolean(p.isDiscount),
          hasUpsell: Boolean(p.showUpsell),
          marketingIntent: p.showUpsell ? "ACCOUNT_UPSELL" : "NONE",
          targetPrice: p.memberPrice ?? null,
          discountLabel: p._audit?.discountLabel ?? null,
          displayUpsell: p._audit?.displayUpsell ?? null,
        };

        return {
          ...p,
          audit: vmAudit,
          __isCave: cave,
          __productScope: cave ? "cave" : "classic",
        };
      }

      const computedAudit = auditPrice(p, {
        profilPricing: profilPricing || {},
        userProfil,
      });

      return {
        ...p,
        audit: computedAudit,
        __isCave: cave,
        __productScope: cave ? "cave" : "classic",
      };
    });
  }, [produits, profilPricing, userProfil]);

  /**
   * ============================================================
   * GUIDE — SPLIT CLASSIQUE / CAVE
   * ============================================================
   */
  const produitsClassiques = useMemo(
    () => produitsAudites.filter((p) => !isCaveProduct(p)),
    [produitsAudites]
  );

  const produitsCave = useMemo(
    () => produitsAudites.filter((p) => isCaveProduct(p)),
    [produitsAudites]
  );

  /**
   * ============================================================
   * GUIDE — RECHERCHE FULLTEXT
   * ============================================================
   *
   * On choisit la bonne base de recherche selon la vue :
   * - alcool  => produits cave
   * - autres  => produits classiques
   */
  const produitsSourceParVue = useMemo(() => {
    if (vueActuelle === "alcool") return produitsCave;
    return produitsClassiques;
  }, [vueActuelle, produitsCave, produitsClassiques]);

  const produitsApresRecherche = useMemo(() => {
    if (!Array.isArray(produitsSourceParVue)) return [];

    if (!recherche?.trim()) {
      return produitsSourceParVue;
    }

    const fuse = new Fuse(produitsSourceParVue, {
      keys: ["name", "category", "description", "histoire"],
      threshold: 0.3,
    });

    return fuse.search(recherche).map((r) => r.item);
  }, [produitsSourceParVue, recherche]);

  /**
   * ============================================================
   * GUIDE — FILTRE PAR VUE
   * ============================================================
   *
   * Règles métier :
   *
   * - accueil   : produits classiques uniquement
   * - magasin   : produits classiques disponibles
   * - produits  : produits classiques (catalogue)
   * - actions   : produits classiques promo / upsell
   * - alcool    : produits cave
   */
  const produitsApresVue = useMemo(() => {
    let filtered = [...produitsApresRecherche];

    switch (vueActuelle) {
      case "magasin":
        filtered = filtered.filter(
          (p) => p.audit?.availabilityStatus === "AVAILABLE"
        );
        break;

      case "actions":
        filtered = filtered.filter(
          (p) => p.audit?.isPromoActive || p.audit?.hasUpsell
        );
        break;

      case "alcool":
        // pas de filtre additionnel ici :
        // la source a déjà été limitée aux produits cave
        break;

      case "produits":
      case "accueil":
      default:
        // catalogue classique / accueil
        break;
    }

    return filtered;
  }, [produitsApresRecherche, vueActuelle]);

  /**
   * ============================================================
   * GUIDE — FILTRE PAR CATÉGORIE
   * ============================================================
   *
   * En vue alcool :
   * - filtre sur catégorie cave
   *
   * En vues classiques :
   * - filtre sur catégorie classique
   */
  const produitsAffiches = useMemo(() => {
    let filtered = [...produitsApresVue];

    if (
      categorieActive &&
      categorieActive !== "TOUT" &&
      vueActuelle !== "actions"
    ) {
      filtered = filtered.filter(
        (p) =>
          normalizeLabel(p.category || p.categorie) ===
          normalizeLabel(categorieActive)
      );
    }

    return filtered;
  }, [produitsApresVue, categorieActive, vueActuelle]);

  /**
   * ============================================================
   * GUIDE — CATÉGORIES À AFFICHER
   * ============================================================
   *
   * - alcool   => catégories cave réelles
   * - produits => catégories classiques venant du backend classique
   * - magasin  => catégories visibles dans les produits classiques filtrés
   * - actions  => pas de catégories utiles
   */
  const categoriesAffichees = useMemo(() => {
    // ----------------------------------------------------------
    // PAGE ALCOOL
    // ----------------------------------------------------------
    if (vueActuelle === "alcool") {
      return [...new Set(produitsCave.map((p) => p.category || p.categorie))]
        .filter(isRealCategory)
        .map((catName) => {
          const safeName = normalizeLabel(catName);
          return {
            name: safeName,
            label: safeName,
            category: safeName,
          };
        });
    }

    // ----------------------------------------------------------
    // PAGE PRODUITS (catalogue classique)
    // ----------------------------------------------------------
    if (vueActuelle === "produits") {
      return (Array.isArray(categories) ? categories : [])
        .filter(isRealCategory)
        .map((catName) => {
          const safeName = normalizeLabel(catName);
          const slug =
            CATEGORY_MAP[safeName] ||
            safeName.toLowerCase().replace(/\s+/g, "_");

          return {
            name: safeName,
            label: t.categories?.[slug] || safeName,
            category: safeName,
          };
        });
    }

    // ----------------------------------------------------------
    // PAGE MAGASIN
    // ----------------------------------------------------------
    if (vueActuelle === "magasin") {
      return [
        ...new Set(produitsAffiches.map((p) => p.category || p.categorie)),
      ]
        .filter(isRealCategory)
        .map((catName) => {
          const safeName = normalizeLabel(catName);
          const slug =
            CATEGORY_MAP[safeName] ||
            safeName.toLowerCase().replace(/\s+/g, "_");

          return {
            name: safeName,
            label: t.categories?.[slug] || safeName,
            category: safeName,
          };
        });
    }

    // ----------------------------------------------------------
    // AUTRES PAGES
    // ----------------------------------------------------------
    return [];
  }, [categories, produitsAffiches, produitsCave, vueActuelle, t]);

  const isAdmin = profilPricing?.isAdmin || false;
  const totalArticles = sousTotal || 0;

  useEffect(() => {
    localStorage.setItem("lang", langue);
  }, [langue]);

  return {
    t,
    langue,
    setLangue,
    modeGestion,
    setModeGestion,
    modeLivraison,
    setModeLivraison,
    vueActuelle,
    setVueActuelle,
    categorieActive,
    setCategorieActive,
    recherche,
    setRecherche,
    produitsAffiches,
    categoriesAffichees,
    catLoading,
    isAdmin,
    isAuthenticated,
    totalArticles,
    userProfil,
    authStatus,
    profilPricing,
    selectedProduct,
    handleProductClick,
    handleCloseModal,
    modals: {
      showAddModal,
      setShowAddModal,
      editProductModal,
      setEditProductModal,
      isSubmitting,
      setIsSubmitting,
      formP,
      setFormP,
    },
  };
};
